import React, { useState } from 'react';
import { List, FunctionField, TextInput, useTranslate, usePermissions } from 'react-admin';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import {
  EditRounded as EditIcon, DeleteOutlineRounded as DeleteIcon,
  GroupOutlined as GroupIcon, SwitchAccountRounded as ImpersonateIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '@/utils/httpClient';
import { getCurrentUser } from '@/utils/auth';
import { canImpersonate, startImpersonation } from '@/utils/impersonation';
import {
  ConfirmDeleteDialog, ListScreen, PageTitle, ResponsiveDatagrid, RowActionsMenu, SectionHeader,
} from '@/components/shell';

interface UserRecord {
  id: string;
  name: string;
  email: string;
  role: string;
  enabled: boolean;
  hasInheritedRoles?: boolean;
}

const UserIdentity: React.FC<{ record: UserRecord }> = ({ record }) => (
  <Box component="span" sx={{ display: 'block' }}>
    <Typography variant="body2" component="span" sx={{ display: 'block' }}>{record.name}</Typography>
    <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
      {record.email}
    </Typography>
  </Box>
);

/** The role in words; a user with no access is the exception, so only that one gets a chip. */
const UserRole: React.FC<{ record: UserRecord }> = ({ record }) => {
  const t = useTranslate();
  const label = t(`roles.${record.role}`, { _: record.role });
  const inherited = t('screens.users.inheritedRoles');
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      {record.role === 'NoAccess' ? (
        <Chip label={label} color="error" size="small" />
      ) : (
        <Typography variant="body2" component="span">{label}</Typography>
      )}
      {record.hasInheritedRoles && (
        <Tooltip title={inherited}>
          <GroupIcon fontSize="small" color="action" aria-label={inherited} role="img" />
        </Tooltip>
      )}
    </Box>
  );
};

const UserActions: React.FC<{ record: UserRecord }> = ({ record }) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { permissions: role } = usePermissions<string>();
  const currentUserId = getCurrentUser()?.id;

  const handleImpersonate = () => {
    startImpersonation({
      sub: record.id,
      displayName: record.email,
      role: record.role,
    });
    // Full reload from the root: the effective role changed, so every
    // mounted admin view is stale.
    window.location.hash = '#/';
    window.location.reload();
  };

  return (
    <>
      <RowActionsMenu
        actions={[
          {
            key: 'impersonate',
            label: t('users.actions.impersonate'),
            icon: <ImpersonateIcon fontSize="small" />,
            onClick: handleImpersonate,
            hidden: !canImpersonate(role, currentUserId, { id: record.id, role: record.role, enabled: record.enabled }),
          },
          {
            key: 'edit',
            label: t('ra.action.edit'),
            icon: <EditIcon fontSize="small" />,
            onClick: () => navigate(`/users/${record.id}`),
          },
          {
            key: 'delete',
            label: t('ra.action.delete'),
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            dividerBefore: true,
            onClick: () => setDeleteOpen(true),
          },
        ]}
      />
      <ConfirmDeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        name={record.name}
        onDelete={() => httpClient.delete(`/admin/user/${record.id}`)}
      />
    </>
  );
};

export const UserList = () => {
  const t = useTranslate();
  const navigate = useNavigate();

  const filters = [<TextInput key="q" source="q" label={t('ra.action.search')} alwaysOn resettable />];

  return (
    <ListScreen
      header={
        <SectionHeader
          left={<PageTitle title={t('resources.users.name', { smart_count: 2 })} subtitle={t('screens.users.subtitle')} />}
          create={{ label: t('common.create'), onClick: () => navigate('/users/create') }}
        />
      }
    >
      <List filters={filters} actions={false} empty={false} perPage={10}>
        <ResponsiveDatagrid<UserRecord>
          rowClick="edit"
          mobile={{
            primary: (r) => <UserIdentity record={r} />,
            lines: [{ label: t('resources.users.fields.role'), render: (r) => <UserRole record={r} /> }],
            actions: (r) => <UserActions record={r} />,
            onClick: (r) => navigate(`/users/${r.id}`),
          }}
        >
          <FunctionField
            label={t('screens.users.user')}
            sortable={false}
            render={(r: UserRecord) => <UserIdentity record={r} />}
          />
          <FunctionField
            label={t('resources.users.fields.role')}
            sortable={false}
            render={(r: UserRecord) => <UserRole record={r} />}
          />
          <FunctionField
            label=""
            textAlign="right"
            sortable={false}
            render={(r: UserRecord) => <UserActions record={r} />}
          />
        </ResponsiveDatagrid>
      </List>
    </ListScreen>
  );
};
